import { ArrowLeft, ArrowUpTray, DocumentText, Trash } from '@medusajs/icons';
import { Badge, Button, Input, Skeleton, StatusBadge, Tabs, Text, toast } from '@pcrm/fe/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { SectionContainer, SectionEmpty, SectionHeader } from '../../components/section';
import { ApiError } from '../../lib/api';
import { useHasPermission } from '../auth/use-session';
import { type EFacturaFilterValue, EFacturaScreen } from '../efactura/efactura-screen';
import {
  COMPANIES_QUERY_KEY,
  type Company,
  companiesQueryOptions,
  companyDisplayName,
  companyLogoUrl,
  hasStructuredAddress,
  removeCompanyLogo,
  uploadCompanyLogo,
} from './companies.api';
import { CompanyFields, useCompanyForm } from './company-form';
import { CompanyIcon } from './company-icons';
import { CompanySignatureSection } from './company-signature-section';
import { CompanySpvSection, type SpvCallbackResult } from './company-spv-section';
import { DocumentNumberingSection } from './document-numbering-section';
import { invoiceModelCatalogQueryOptions } from './invoice-model.api';
import { InvoiceModelModal } from './invoice-model-modal';
import { InvoiceSeriesSection } from './invoice-series-section';

/**
 * Tabul deschis, **ca parametru de căutare în URL**, ca la fișa clientului: un refresh lasă
 * operatorul pe tabul pe care era.
 *
 * `.catch('setari')`: un `?tab=` stricat deschide firma pe primul tab, nu o eroare de rutare.
 */
export const companyTabSchema = z.enum(['setari', 'financiar', 'spv', 'efactura']).catch('setari');

export type CompanyTab = z.infer<typeof companyTabSchema>;

/**
 * Ce validează ruta `/setari/firme/$companyId`.
 *
 * `spv` și `spvMessage` vin doar de la callback-ul ANAF (#200), care întoarce browserul pe tabul
 * SPV cu rezultatul conectării. Ecranul îl spune o dată, apoi îl scoate din adresă.
 */
export const companySearchSchema = z.object({
  tab: companyTabSchema.default('setari'),
  spv: z.enum(['connected', 'refused', 'unreachable']).optional().catch(undefined),
  spvMessage: z.string().optional().catch(undefined),
});

const COMPANY_TABS: readonly { readonly value: CompanyTab; readonly label: string }[] = [
  { value: 'setari', label: 'Setări firmă' },
  { value: 'financiar', label: 'Financiar' },
  { value: 'spv', label: 'SPV' },
  { value: 'efactura', label: 'e-Factura' },
];

/**
 * Ecranul unei firme a grupului (#207), cu taburi ca fișa clientului.
 *
 * Înainte, setările unei firme stăteau în trei locuri deschise din meniul rândului —
 * sertarul de date, sertarul „Semnătură" și fereastra „Model factură" — iar sertarul de date
 * ajunsese să poarte și logo-ul, și seria. Acum: **Setări firmă** are ce ține de identitatea
 * ei (date, adresă, logo, semnătură), **Financiar** ce ține de facturare (seria, modelul).
 *
 * ⚠️ **Antetul n-are „Salvează".** Pe fișa clientului îl are, pentru că acolo un singur
 * formular acoperă toate taburile. Aici aproape nimic nu trece prin formular: logo-ul,
 * semnătura, seria și modelul se salvează pe loc (#188), fiecare cu butonul lui. Un
 * „Salvează" în antet ar fi părut că le poartă și pe ele; stă pe cardul datelor, singurul pe
 * care îl salvează.
 *
 * Firma se citește din lista de administrare, nu dintr-un endpoint de detaliu: lista e deja
 * în cache (selectorul din sidebar o cere oricum) și cuprinde și firmele dezactivate.
 */
export function CompanyScreen({
  companyId,
  tab,
  onTabChange,
  spvCallback = null,
}: {
  companyId: string;
  tab: CompanyTab;
  onTabChange: (tab: CompanyTab) => void;
  /** Rezultatul callback-ului ANAF, dacă browserul tocmai s-a întors de acolo. */
  spvCallback?: SpvCallbackResult | null;
}) {
  const { data, isPending, error } = useQuery(companiesQueryOptions());
  const company = data?.find((item) => item.id === companyId);
  const canViewInvoices = useHasPermission()('invoices.view');

  if (isPending) {
    return (
      <SectionContainer>
        <div className="flex flex-col gap-3 px-6 py-6">
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-2/3" />
        </div>
      </SectionContainer>
    );
  }

  if (error || company === undefined) {
    return (
      <SectionContainer>
        <SectionEmpty tone="error">
          {error instanceof Error ? error.message : 'Firma nu există.'}
        </SectionEmpty>
      </SectionContainer>
    );
  }

  return (
    <Tabs
      value={tab}
      onValueChange={(value) => onTabChange(companyTabSchema.parse(value))}
      className="flex flex-1 flex-col gap-4 sm:gap-6"
    >
      <SectionContainer>
        <SectionHeader
          title={
            <span className="flex items-center gap-x-2">
              <CompanyIcon icon={company.icon} />
              {companyDisplayName(company)}
              <StatusBadge color={company.status ? 'green' : 'red'}>
                {company.status ? 'Activă' : 'Dezactivată'}
              </StatusBadge>
              {hasStructuredAddress(company) ? null : (
                <Badge size="2xsmall" color="orange">
                  Fără adresă structurată
                </Badge>
              )}
            </span>
          }
          subtitle={company.name}
          actions={
            <Button size="small" variant="secondary" asChild>
              <Link to="/setari/firme">
                <ArrowLeft />
                Înapoi la listă
              </Link>
            </Button>
          }
        />

        <div className="px-6 py-3">
          <Tabs.List>
            {COMPANY_TABS.filter(
              // e-Factura cere `invoices.view`, ca ruta lui din Conta; fișa firmei cere doar
              // `companies.view`.
              (item) => item.value !== 'efactura' || canViewInvoices,
            ).map((item) => (
              <Tabs.Trigger key={item.value} value={item.value}>
                {item.label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
        </div>
      </SectionContainer>

      <Tabs.Content value="setari" className="flex flex-col gap-4 sm:gap-6">
        {/* `key`: alt id, alt formular — vezi `useCompanyForm`. */}
        <CompanyDataSection key={company.id} company={company} />

        {/*
          Logo-ul stă lângă restul identității firmei, și **nu** în setările pe firmă: acolo un
          rând lipsă înseamnă „moștenește globalul", iar pentru logo asta ar fi însemnat că o
          firmă fără logo tipărește logo-ul altcuiva pe factura ei. Vezi tichetul #173.
        */}
        <SectionContainer>
          <SectionHeader
            title="Logo"
            subtitle="Se salvează la încărcare, separat de datele firmei."
          />
          <div className="px-6 py-4">
            <CompanyLogoField company={company} />
          </div>
        </SectionContainer>

        <CompanySignatureSection company={company} />

        {/*
          Pragul numerotării automate (#239): sub el se încarcă, cu numărul lor, contractele
          din sistemul vechi. Se salvează pe loc, ca seria de facturi.
        */}
        <DocumentNumberingSection company={company} />
      </Tabs.Content>

      <Tabs.Content value="financiar" className="flex flex-col gap-4 sm:gap-6">
        {/*
          Seria nu e un câmp al firmei: stă în modulul de facturare, cu contorul ei, și se
          salvează pe loc, cu butonul ei. Vezi tichetul #188.
        */}
        <SectionContainer>
          <SectionHeader title="Seria de facturi" />
          <div className="px-6 py-4">
            <InvoiceSeriesSection company={company} disabled={false} />
          </div>
        </SectionContainer>

        <InvoiceModelSection company={company} />
      </Tabs.Content>

      <Tabs.Content value="spv" className="flex flex-col gap-4 sm:gap-6">
        {/*
          Conexiunea la SPV (#200): tokenurile stau în modulul de facturare, cifrate. „Scoate
          rezultatul din adresă" e o schimbare de tab pe același tab.
        */}
        <CompanySpvSection
          company={company}
          callback={spvCallback}
          onCallbackSeen={() => onTabChange('spv')}
        />
      </Tabs.Content>

      <Tabs.Content value="efactura" className="flex flex-col gap-4 sm:gap-6">
        {/*
          Facturile firmei și starea lor în SPV (#240): același ecran ca în Conta, cu firma fixă.
          Filtrele stau în ecran, nu în adresă: adresa fișei ține doar tabul.
        */}
        <CompanyEFactura companyId={company.id} />
      </Tabs.Content>
    </Tabs>
  );
}

/**
 * Datele firmei, cu „Salvează" **pe card**: e singurul lucru de pe ecran care trece prin el.
 *
 * Fără „Renunță": pe un ecran nu e nimic de închis. Cine nu vrea ce a tastat pleacă de pe
 * ecran, ca la fișa clientului.
 */
function CompanyDataSection({ company }: { company: Company }) {
  const state = useCompanyForm({ company });

  return (
    <SectionContainer>
      <SectionHeader
        title="Datele firmei"
        actions={
          <Button
            size="small"
            onClick={() => state.mutation.mutate()}
            isLoading={state.mutation.isPending}
            disabled={!state.canSave || state.mutation.isPending}
          >
            Salvează
          </Button>
        }
      />

      <div className="flex max-w-2xl flex-col gap-y-4 px-6 py-4">
        <CompanyFields state={state} />
      </div>
    </SectionContainer>
  );
}

/**
 * Modelul de factură: ce e ales acum, și butonul spre ecranul de alegere.
 *
 * Alegerea rămâne în **fereastra mare** (`InvoiceModelModal`), nu se mută în tab: are nevoie
 * de coala A4 alături, care se schimbă cât alegi, iar un tab pe lățimea conținutului n-ar avea
 * loc de ea lângă opțiuni.
 */
function InvoiceModelSection({ company }: { company: Company }) {
  const [open, setOpen] = useState(false);
  const catalog = useQuery(invoiceModelCatalogQueryOptions());

  const layout = catalog.data?.layouts.find((item) => item.id === company.invoiceTheme.layout);
  const accent = catalog.data?.accents.find((item) => item.id === company.invoiceTheme.accent);

  return (
    <SectionContainer>
      <SectionHeader
        title="Modelul de factură"
        actions={
          <Button size="small" variant="secondary" onClick={() => setOpen(true)}>
            <DocumentText />
            Alege modelul
          </Button>
        }
      />

      <div className="flex items-center gap-x-2 px-6 py-4">
        {catalog.isPending ? (
          <Skeleton className="h-5 w-48" />
        ) : (
          <>
            {accent ? (
              <span
                aria-hidden
                className="size-3 shrink-0 rounded-full"
                style={{ backgroundColor: accent.hex }}
              />
            ) : null}
            <Text size="small">
              {[layout?.label ?? company.invoiceTheme.layout, accent?.label]
                .filter(Boolean)
                .join(' · ')}
            </Text>
          </>
        )}
      </div>

      <InvoiceModelModal company={open ? company : null} onClose={() => setOpen(false)} />
    </SectionContainer>
  );
}

/**
 * Logo-ul firmei: previzualizare, încărcare, înlocuire, scoatere.
 *
 * **Se salvează pe loc**, separat de formular: e un fișier, iar butonul „Salvează" al fișei
 * nu-l poartă. Consecința vizibilă e că o încărcare rămâne făcută chiar dacă operatorul
 * pleacă de pe ecran fără să salveze datele — la fel ca la ștampilă.
 *
 * Cheia logo-ului se citește din `company`, care vine din lista recitită după fiecare
 * încărcare — nu dintr-o copie locală. În sertarul de dinainte de #207 era copie, pentru că
 * firma sertarului era rândul de la deschidere și nu se mai împrospăta.
 */
function CompanyLogoField({ company }: { company: Company }) {
  const queryClient = useQueryClient();
  const logoFile = company.logoFile;
  const [file, setFile] = useState<File | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY });

  const onError = (error: unknown) =>
    toast.error(error instanceof ApiError ? error.message : 'Logo-ul nu a putut fi salvat.');

  const upload = useMutation({
    mutationFn: () => {
      if (!file) {
        throw new Error('Nu s-a ales niciun fișier.');
      }

      return uploadCompanyLogo({ companyId: company.id, file });
    },
    onSuccess: async () => {
      toast.success('Logo încărcat');
      setFile(null);
      await invalidate();
    },
    onError,
  });

  const remove = useMutation({
    mutationFn: () => removeCompanyLogo(company.id),
    onSuccess: async () => {
      toast.success('Firma tipărește de acum fără logo');
      setFile(null);
      await invalidate();
    },
    onError,
  });

  const isBusy = upload.isPending || remove.isPending;

  return (
    <div className="flex flex-col gap-y-2">
      {logoFile ? (
        // Fundalul în tablă de șah, ca la ștampilă: un PNG cu transparență pe alb n-ar
        // lăsa să se vadă dacă s-a încărcat o imagine goală.
        <img
          src={companyLogoUrl(company.id, logoFile)}
          alt={`Logo ${companyDisplayName(company)}`}
          className="max-h-16 max-w-[200px] self-start rounded border border-ui-border-base bg-[repeating-conic-gradient(#f3f4f6_0%_25%,#ffffff_0%_50%)] bg-[length:12px_12px] object-contain p-1"
        />
      ) : null}

      <Input
        id="company-logo"
        type="file"
        // `accept` filtrează doar dialogul; formatul se verifică pe server, după conținut,
        // nu după extensie. SVG nu e în listă nici acolo.
        accept="image/png,image/jpeg,image/gif,image/webp"
        onChange={(event) => setFile(event.target.files?.[0] ?? null)}
        disabled={isBusy}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="small"
          variant="secondary"
          onClick={() => upload.mutate()}
          isLoading={upload.isPending}
          disabled={isBusy || file === null}
        >
          <ArrowUpTray />
          {logoFile ? 'Înlocuiește' : 'Încarcă'}
        </Button>

        {logoFile ? (
          <Button
            size="small"
            variant="transparent"
            onClick={() => remove.mutate()}
            isLoading={remove.isPending}
            disabled={isBusy}
          >
            <Trash />
            Scoate logo-ul
          </Button>
        ) : null}
      </div>

      <Text size="small" className="text-ui-fg-subtle">
        PNG, JPEG, GIF sau WebP, cel mult 2 MB. Un logo vectorial se convertește la PNG înainte. Se
        afișează pe facturile emise de firmă. Contractele și anexele nu-l folosesc.
      </Text>
    </div>
  );
}

function CompanyEFactura({ companyId }: { companyId: string }) {
  const [filter, setFilter] = useState<EFacturaFilterValue | undefined>(undefined);
  const [month, setMonth] = useState<string | undefined>(undefined);

  return (
    <EFacturaScreen
      fixedCompanyId={companyId}
      companyId={companyId}
      filter={filter}
      month={month}
      onFilterChange={setFilter}
      onMonthChange={setMonth}
    />
  );
}
